import type { CompanyInfo } from '@mt/shared';
import { Building2, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/states';
import { api } from '@/lib/api';
import type { Customer } from '@/lib/types';
import { checkDuplicates, DUPLICATE_SIGNAL_LABELS, type DuplicateCandidate } from '../customers/api';
import { getCustomer } from '../quotes/api';

interface Props {
  info: CompanyInfo | null;
  onClose: () => void;
  /** Chọn khách có sẵn */
  onUse: (c: Customer) => void;
  /** Không trùng / NV muốn tạo mới → mở hộp Thêm khách hàng điền sẵn */
  onCreate: (info: CompanyInfo) => void;
  /**
   * Dán ở ô Khách hàng (anh Tiến 07/10/2026 "dán ảnh luôn ở mục khách hàng và tự lưu lại"):
   * không có khách trùng → tự tạo khách mới và chọn luôn; trùng MST với khách trong phạm vi → chọn luôn khách đó.
   * Nghi trùng (SĐT, email, tên) → vẫn hỏi NV.
   */
  auto?: boolean;
}

/** Trường gửi lên khi tạo khách từ thông tin dán: số ĐT thứ 2 trở đi, website ghi vào ghi chú. */
export function customerFieldsOf(info: CompanyInfo) {
  return {
    name: info.name,
    taxCode: info.taxCode ?? '',
    address: info.address ?? '',
    phone: info.phone ?? '',
    email: info.email ?? '',
    note: [info.phones.length > 1 && `ĐT khác: ${info.phones.slice(1).join(', ')}`, info.website && `Web: ${info.website}`].filter(Boolean).join(' · '),
  };
}

/**
 * Dán khối thông tin công ty vào Báo giá nhanh (anh Tiến 07/10/2026): so với khách sẵn có (MST, SĐT, email, tên) trước,
 * trùng thì dùng khách đó, không trùng thì tạo khách mới đã điền sẵn. Không bao giờ tự tạo hay tự gộp.
 */
export function CustomerPasteDialog({ info, onClose, onUse, onCreate, auto }: Props) {
  const [cands, setCands] = useState<DuplicateCandidate[] | null>(null);
  const [picking, setPicking] = useState<number | null>(null);

  useEffect(() => {
    if (!info) return;
    let live = true;
    setCands(null);
    const base = { name: info.name, ...(info.taxCode ? { taxCode: info.taxCode } : {}), ...(info.email ? { email: info.email } : {}), ...(info.address ? { address: info.address } : {}) };
    // Mỗi số điện thoại kiểm tra một lần (API nhận 1 số), gộp theo khách
    const calls = [checkDuplicates({ ...base, ...(info.phones[0] ? { phone: info.phones[0] } : {}) }), ...info.phones.slice(1, 3).map((phone) => checkDuplicates({ phone }))];
    Promise.all(calls)
      .then((lists) => {
        const byKey = new Map<string, DuplicateCandidate>();
        for (const c of lists.flat()) {
          const k = String(c.id ?? c.code);
          const cur = byKey.get(k);
          byKey.set(k, cur ? { ...cur, signals: [...new Set([...cur.signals, ...c.signals])] } : c);
        }
        // MST trùng lên đầu, rồi SĐT, email, tên
        const rank = (c: DuplicateCandidate) => (c.signals.includes('tax_code') ? 0 : c.signals.includes('phone') ? 1 : c.signals.includes('email') ? 2 : 3);
        const list = [...byKey.values()].sort((a, b) => rank(a) - rank(b));
        if (!live) return;
        setCands(list);
        if (!auto) return;
        const sameTax = list.filter((c) => c.signals.includes('tax_code'));
        if (sameTax.length === 1 && sameTax[0]!.inScope && sameTax[0]!.id) void use(sameTax[0]!);
        else if (!list.length) void autoCreate(info);
      })
      .catch((e: Error) => {
        if (live) setCands([]);
        toast.error(`Không kiểm tra được khách trùng: ${e.message}`);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  const [creating, setCreating] = useState(false);
  const autoCreate = async (i: CompanyInfo) => {
    setCreating(true);
    try {
      const f = customerFieldsOf(i);
      const c = await api.post<Customer>('/customers', { ...f, email: f.email || null, note: f.note || null, customerType: 'company', contacts: [] });
      toast.success(`Đã tự lưu khách hàng mới ${c.code} – ${c.name}`);
      onUse(c);
    } catch (e) {
      // Dữ liệu chưa hợp lệ (VD email sai) / vừa có người tạo trùng → để NV sửa trong hộp Thêm khách hàng
      toast.error(`Chưa tự lưu được khách: ${(e as Error).message}`);
      onCreate(i);
    } finally {
      setCreating(false);
    }
  };

  const use = async (c: DuplicateCandidate) => {
    if (!c.id) return;
    setPicking(c.id);
    try {
      onUse(await getCustomer(c.id));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPicking(null);
    }
  };

  const exact = cands?.some((c) => c.signals.includes('tax_code'));
  return (
    <Dialog
      open={!!info}
      onOpenChange={(v) => !v && onClose()}
      title="Thông tin khách vừa dán"
      description="Kiểm tra khách đã có trong danh mục trước khi tạo mới."
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Bỏ qua
          </Button>
          {info && !exact && (
            <Button onClick={() => onCreate(info)} disabled={!cands} data-testid="paste-create-customer">
              <UserPlus /> Tạo khách mới
            </Button>
          )}
        </>
      }
    >
      {info && (
        <div className="space-y-3 text-sm">
          <div className="rounded-md border bg-muted/40 p-3">
            <div className="flex items-center gap-2 font-semibold">
              <Building2 className="size-4 text-primary" /> {info.name}
            </div>
            <dl className="mt-1.5 grid grid-cols-[90px_1fr] gap-x-2 gap-y-0.5 text-xs">
              {info.taxCode && (
                <>
                  <dt className="text-muted-foreground">MST</dt>
                  <dd>{info.taxCode}</dd>
                </>
              )}
              {info.address && (
                <>
                  <dt className="text-muted-foreground">Địa chỉ</dt>
                  <dd>{info.address}</dd>
                </>
              )}
              {info.phones.length > 0 && (
                <>
                  <dt className="text-muted-foreground">Điện thoại</dt>
                  <dd>{info.phones.join(' · ')}</dd>
                </>
              )}
              {info.email && (
                <>
                  <dt className="text-muted-foreground">Email</dt>
                  <dd>{info.email}</dd>
                </>
              )}
            </dl>
          </div>

          {!cands ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Spinner /> Đang tìm khách trùng (MST, SĐT, email, tên)...
            </div>
          ) : creating ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Spinner /> Không có khách trùng, đang tự lưu khách mới...
            </div>
          ) : !cands.length ? (
            <p className="text-success">Chưa có khách nào trùng. Bấm "Tạo khách mới" để thêm (hệ thống cấp mã tạm TN-).</p>
          ) : (
            <div className="space-y-1.5">
              <p className="font-medium">{exact ? 'MST đã có trong danh mục: dùng khách này.' : `Có ${cands.length} khách có thể trùng:`}</p>
              <ul className="divide-y rounded border">
                {cands.map((c, i) => (
                  <li key={c.id ?? `${c.code}-${i}`} className="flex flex-wrap items-center gap-2 px-2 py-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium">
                          {c.code} · {c.name}
                        </span>
                        {c.signals.map((s) => (
                          <Badge key={s} variant={s === 'tax_code' || s === 'phone' ? 'error' : 'warning'}>
                            {DUPLICATE_SIGNAL_LABELS[s] ?? s}
                          </Badge>
                        ))}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {c.inScope
                          ? [c.taxCode && `MST ${c.taxCode}`, c.phone, c.address].filter(Boolean).join(' · ')
                          : `Khách của ${c.ownerName ?? 'nhân viên khác'}: nhờ trưởng phòng chuyển hoặc báo giá giúp`}
                      </div>
                    </div>
                    {c.inScope && c.id && (
                      <Button size="sm" variant="outline" disabled={picking !== null} onClick={() => void use(c)}>
                        {picking === c.id && <Spinner />} Dùng khách này
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
